import { Dispatch, SetStateAction } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { EnumTechPlatform } from 'tdc-web-backend/enums/enums';
import { CompanyDto } from 'tdc-web-backend/companies/schemas';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import { ReactComponent as SuitcaseCheckIcon } from '../../../../../../../../../../assets/icons/layout-icons/SuitcaseCheckIcon.svg';
import { primaryLight } from '../../../../../../../../../../utils/color';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import { mapTechPlatform } from '../../../../../../../../../../utils/enums';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';

interface CompanyTechPlatformsProps {
  isEditModalOpen: boolean;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
}

const CompanyTechPlatformsEditModal = ({
  isEditModalOpen,
  setIsEditModalOpen,
}: CompanyTechPlatformsProps) => {
  const refresh = useRefresh();
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const { t } = useTranslation();
  const methods = useForm<Pick<CompanyDto, 'techPlatforms'>>({
    mode: 'onChange',
    defaultValues: {
      techPlatforms: companyData?.techPlatforms ?? ([] as EnumTechPlatform[]),
    },
  });

  const onSubmit = (data: Pick<CompanyDto, 'techPlatforms'>) => {
    updateCompany(
      { techPlatforms: data.techPlatforms },
      {
        onSuccess: () => {
          refresh();
          setIsEditModalOpen(false);
        },
      },
    );
  };

  return (
    <ConfirmModal
      width="40%"
      title={t('settings.companyProfile.techPlatforms')}
      onCancel={() => setIsEditModalOpen(false)}
      onConfirm={methods.handleSubmit(onSubmit)}
      isModalOpen={isEditModalOpen}
      setIsModalOpen={setIsEditModalOpen}
      automaticallyCloseModalOnButtonClick={false}
      isLoading={isLoadingUpdateCompany}
      confirmButtonText={t('common.save')}
      iconProps={{
        icon: <SuitcaseCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: primaryLight[100],
      }}
      message={
        <FormProvider {...methods}>
          <Box component="form">
            <CustomInput
              input={<CustomInput.Autocomplete />}
              choices={mapTechPlatform}
              name="techPlatforms"
              label={t('settings.companyProfile.techPlatforms')}
              multiple
              placeholder={t('common.select')}
              sx={{ width: '100%' }}
            />
          </Box>
        </FormProvider>
      }
    />
  );
};

export default CompanyTechPlatformsEditModal;
